import React, { useState, useEffect, useRef } from 'react'
import Sidebar from './components/Sidebar.js'
import Upsell from './components/Upsell.js'
import { useSelector, useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { getData, postData } from '../../../js/helpers/apiHelper'
import * as lib from '../../../js/lib/lib'
import {
  setMilesNBonusAccount,
  setSelectedVehicle,
  setHasFlightNumber,
  resetMBData,
  resetMilesNBonusAccount,
} from '../../../js/lib/redux/slices/booking'
import $ from 'jquery'

import DriverDetails, {
  driverDetailsSchema,
} from './components/DriverDetails.js'
import BillingAddress, {
  billingAddressSchema,
} from './components/BillingAddress.js'
import FlightDetails, {
  flightDetailsSchema,
} from './components/FlightDetails.js'
import CommentsSection, {
  commentsSectionSchema,
} from './components/CommentsSection.js'
import Payment, { paymentSchema } from './components/Payment.js'
import MilesNCash from './components/MilesNCash.js'
import MilesNCashOTPModal from './components/MilesNCashOTPModal'
import Alert from '../Base/Alert'
import Terms from '../Terms/index.js'
import { z } from 'zod'
import { CircularProgress } from '@mui/material'
import SummaryMobilePanel from './components/SummaryMobilePanel'
import showMilesNBonus from '../../../js/helpers/showMilesNBonus.js'

const BookingStep3 = () => {
  const {
    configuration,
    dictionary,
    token,
    flightList,
    pickUpDetails,
    dropOffDetails,
  } = window.bookingData
  const {
    isPrepaid,
    selectedVehicle,
    selectedExtras,
    hasFlightNumber,
    mbData,
    milesNBonusAccount,
  } = useSelector((state) => state.booking)

  const [isLoading, setIsLoading] = useState(false)
  // const [isMarketing, setIsMarketing] = useState(false)
  const [showPayment, setShowPayment] = useState(true)
  const [showBillingInformation, setShowBillingInformation] = useState(false)
  const [openMilesNCashOTPModal, setOpenMilesNCashOTPModal] = useState(false)
  const [selectedMiles, setSelectedMiles] = useState(0)
  const [formDataForOTP, setFormDataForOTP] = useState(null)
  const [remoteUrl, setRemoteUrl] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [alertMessages, setAlertMessages] = useState([])
  const [showReservationModal, setShowReservationModal] = useState(false)

  // Create refs for collapsible sections
  const driverDetailsRef = useRef(null)
  const billingAddressRef = useRef(null)
  const flightDetailsRef = useRef(null)
  const commentsSectionRef = useRef(null)

  useEffect(() => {
    setSelectedMiles(mbData.redeemAmount || 0)
  }, [mbData])

  const internationalEngine = !selectedVehicle?.isGREngine

  const termsSchema = z.object({
    terms: z
      .boolean({
        required_error: dictionary.termsRequiredText,
        invalid_type_error: dictionary.termsRequiredText,
      })
      .refine((val) => val === true, {
        message: dictionary.termsRequiredText,
      }),
  })

  const getFullSchema = () => {
    let schema = driverDetailsSchema(milesNBonusAccount, mbData, dictionary)
      .and(flightDetailsSchema(dictionary))
      .and(commentsSectionSchema(dictionary))
      .and(termsSchema)

    if (showBillingInformation) {
      schema = schema.and(billingAddressSchema(dictionary, selectedVehicle))
    }

    if (showPayment) {
      schema = schema.and(paymentSchema(dictionary))
    }

    return schema
  }

  const [currentSchema, setCurrentSchema] = useState(getFullSchema())

  const dispatch = useDispatch()

  const {
    handleSubmit,
    control,
    trigger,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(currentSchema),
    mode: 'onTouched',
    defaultValues: {
      terms: false,
    },
  })

  // Expand accordions when there are validation errors
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      // Check which sections have errors and expand them
      const driverDetailsFields = [
        'firstName',
        'lastName',
        'emailGroup',
        'mobilePhone',
        'mobileDialingCode',
        'landPhone',
        'landDialingCode',
      ]
      const billingAddressFields = [
        'address',
        'streetNumber',
        'addressLine',
        'additionalAddressLine',
        'country',
        'region',
        'city',
        'postalCode',
      ]
      const flightDetailsFields = ['airline', 'flightNumber']
      const commentsSectionFields = ['comments']

      let hasExpandedSection = false

      // Expand driver details if any of its fields have errors
      if (
        driverDetailsFields.some((field) => {
          if (field === 'emailGroup') {
            return errors.emailGroup !== undefined
          }
          return errors[field] !== undefined
        })
      ) {
        driverDetailsRef.current?.expand()
        hasExpandedSection = true
      }

      // Expand billing address if any of its fields have errors
      if (billingAddressFields.some((field) => errors[field] !== undefined)) {
        billingAddressRef.current?.expand()
        hasExpandedSection = true
      }

      // Expand flight details if any of its fields have errors
      if (flightDetailsFields.some((field) => errors[field] !== undefined)) {
        flightDetailsRef.current?.expand()
        hasExpandedSection = true
      }

      // Expand comments section if it has errors
      if (commentsSectionFields.some((field) => errors[field] !== undefined)) {
        commentsSectionRef.current?.expand()
        hasExpandedSection = true
      }

      // Scroll to first error after accordions have expanded
      if (hasExpandedSection) {
        setTimeout(() => {
          lib.scrollToValidation(125)
        }, 350) // Wait for accordion animation (300ms) + buffer
      }
    }
  }, [errors])

  useEffect(() => {
    const updatedSchema = getFullSchema()

    setCurrentSchema(updatedSchema)
    reset(undefined, {
      keepValues: true,
      keepDirty: true,
      keepTouched: true,
      keepErrors: true,
      keepDirtyValues: true,
    })
  }, [showBillingInformation, showPayment, selectedVehicle, reset])

  const [isInitialRender, setIsInitialRender] = useState(true)
  const [prevRedeemEnabled, setPrevRedeemEnabled] = useState(
    mbData?.redeemEnabled
  )

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false)
      return
    }

    if (mbData && prevRedeemEnabled !== mbData.redeemEnabled) {
      const updatedSchema = getFullSchema()
      setCurrentSchema(updatedSchema)

      if (mbData.redeemEnabled) {
        setTimeout(() => {
          trigger('firstName')
          trigger('lastName')
        }, 0)
      } else {
        reset(undefined, {
          keepValues: true,
          keepDirty: true,
          keepTouched: true,
          keepErrors: false,
          keepDirtyValues: true,
        })
      }

      setPrevRedeemEnabled(mbData.redeemEnabled)
    }
  }, [mbData?.redeemEnabled, isInitialRender])

  const handleEmailBlur = async (e) => {
    const email = e.target.value

    // Only check if email is valid
    if (!email || !email.includes('@')) {
      return
    }

    try {
      // Construct datetime strings from pickUpDetails and dropOffDetails
      const pickUpDateTime = pickUpDetails
        ? `${pickUpDetails.date} ${pickUpDetails.time}`
        : ''
      const dropOffDateTime = dropOffDetails
        ? `${dropOffDetails.date} ${dropOffDetails.time}`
        : ''

      const payload = {
        brandName: selectedVehicle?.brandName,
        outDate: pickUpDateTime, // Pickup date
        inDate: dropOffDateTime, // DropOff date
        outShop: pickUpDetails?.code, // Pickup location code
        inShop: dropOffDetails?.code, // DropOff location code
        email,
      }
      const response = await postData(
        configuration.endpointCheckReservation,
        payload
      )

      const { hasError, errorMessages, data } = response

      if (hasError) {
        handleError(errorMessages)
        return
      }

      if (data?.hasDuplicateReservation) {
        setShowReservationModal(true)
      }
      handleSuccess()
    } catch (err) {
      console.error(err)
    }
  }

  const onSubmit = (data) => {
    if (mbData.redeemEnabled) {
      handleRequestOTP(data)
    } else {
      handleFormSubmit(data)
    }
  }

  const handleRequestOTP = (data) => {
    // Store the form data for later use in OTP verification
    setFormDataForOTP(data)
    // Call requestOTP with the form data
    requestOTP(data)
  }

  const requestOTP = async (data) => {
    try {
      setTimeout(() => {
        if (isLoading) {
          return false
        }
      }, 0)

      // Use the stored form data if no data is provided (for resend OTP case)
      const formData = data || formDataForOTP
      if (!formData) {
        console.error('BookingStep3 - requestOTP - No form data available')
        return { hasError: true, errorMessages: ['No form data available'] }
      }
      setIsLoading(true)

      var wSize = 600
      const body = document.body.width

      if (body < 600) wSize = 600
      if (body < 500) wSize = 390
      if (body < 300) wSize = 250

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.emailGroup.email,
        verifyEmail: formData.emailGroup.verifyEmail,
        flightNumber: formData.flightNumber,
        comments: formData.comments,
        airline: formData.airline,
        // marketing: isMarketing,
        useMilesRedemption: true,
        redeemedMiles: mbData.redeemAmount,
        referenceNumber: referenceNumber,

        // Navigator data
        navigatorLanguage: navigator.language,
        navigatorColors: screen.colorDepth,
        navigatorHeight: screen.height,
        navigatorWidth: screen.width,
        navigatorTimezone: new Date().getTimezoneOffset(),
        isJavaEnabled: navigator.javaEnabled(),
        challengeWindowSize: wSize,
        token: token,
      }

      if (shouldShowPayment) {
        appendCreditCardData(payload, formData)
      } else {
        appendNoCardData(payload, formData)
      }

      const response = await postData(configuration.endpointSubmitForm, payload)

      const { HasError, Errors } = response

      if (HasError) {
        handleError(Errors)
        return response
      }

      setOpenMilesNCashOTPModal(true)
      handleSuccess()
      return response
    } catch (err) {
      console.error(err)
      handleError([err])
    }
  }

  const verifyOTP = async (otpCode) => {
    try {
      setTimeout(() => {
        if (isLoading) {
          return false
        }
      }, 0)

      // Use the stored form data instead of getting it from the form
      if (!formDataForOTP) {
        console.error('BookingStep3 - verifyOTP - No form data available')
        return false
      }

      setIsLoading(true)

      var wSize = 600
      const body = document.body.width

      if (body < 600) wSize = 600
      if (body < 500) wSize = 390
      if (body < 300) wSize = 250

      const payload = {
        firstName: formDataForOTP.firstName,
        lastName: formDataForOTP.lastName,
        email: formDataForOTP.emailGroup.email,
        verifyEmail: formDataForOTP.emailGroup.verifyEmail,
        flightNumber: formDataForOTP.flightNumber,
        comments: formDataForOTP.comments,
        airline: formDataForOTP.airline,
        // marketing: isMarketing,
        useMilesRedemption: true,
        redeemedMiles: mbData.redeemAmount,
        otpCode: otpCode,
        referenceNumber: referenceNumber,

        // Navigator data
        navigatorLanguage: navigator.language,
        navigatorColors: screen.colorDepth,
        navigatorHeight: screen.height,
        navigatorWidth: screen.width,
        navigatorTimezone: new Date().getTimezoneOffset(),
        isJavaEnabled: navigator.javaEnabled(),
        challengeWindowSize: wSize,
        token: token,
      }

      if (shouldShowPayment) {
        appendCreditCardData(payload, formDataForOTP)
      } else {
        appendNoCardData(payload, formDataForOTP)
      }

      const response = await postData(
        configuration.endpointConfirmMBRedemption,
        payload
      )

      const { HasError, Url, RemoteUrl } = response

      if (HasError) {
        setIsLoading(false)
        return response
      }

      setOpenMilesNCashOTPModal(false)

      if (Url) {
        window.location.assign(Url)
      }
      if (RemoteUrl) {
        lib.scrollToTop()
        setRemoteUrl(RemoteUrl)
        paymentShowIframe(RemoteUrl)
      }

      handleSuccess()
      return response
    } catch (err) {
      console.error(err)
      handleError([err])
    }
  }

  const handleFormSubmit = async (data) => {
    try {
      var wSize = 600
      const body = document.body.width

      if (body < 600) wSize = 600
      if (body < 500) wSize = 390
      if (body < 300) wSize = 250

      const payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.emailGroup.email,
        verifyEmail: data.emailGroup.verifyEmail,
        flightNumber: data.flightNumber,
        comments: data.comments,
        airline: data.airline,
        // marketing: isMarketing,
        useMilesRedemption: mbData?.redeemEnabled,
        redeemedMiles: mbData?.redeemEnabled ? mbData?.redeemAmount : 0,

        // Navigator data
        navigatorLanguage: navigator.language,
        navigatorColors: screen.colorDepth,
        navigatorHeight: screen.height,
        navigatorWidth: screen.width,
        navigatorTimezone: new Date().getTimezoneOffset(),
        isJavaEnabled: navigator.javaEnabled(),
        challengeWindowSize: wSize,
        token: token,
      }

      if (shouldShowPayment) {
        appendCreditCardData(payload, data)
      } else {
        appendNoCardData(payload, data)
      }

      setIsLoading(true)

      const response = await postData(configuration.endpointSubmitForm, payload)

      const { HasError, Errors, Url, RemoteUrl, ReferenceNumber } = response

      if (HasError) {
        handleError(Errors)
        return
      }

      if (mbData?.redeemEnabled) {
        setReferenceNumber(ReferenceNumber)
        setOpenMilesNCashOTPModal(true)
      } else {
        if (Url) {
          window.location.assign(Url)
        }
        if (RemoteUrl) {
          lib.scrollToTop()
          setRemoteUrl(RemoteUrl)
          paymentShowIframe(RemoteUrl)
        }
      }
      handleSuccess()
    } catch (err) {
      console.error(err)
      handleError([err.message])
    }
  }

  const handleSuccess = () => {
    setAlertMessages([])
    setIsLoading(false)
  }

  const handleError = (messages) => {
    if (messages === null || messages === undefined) {
      setAlertMessages([dictionary.defaultErrorMessage])
    } else if (typeof messages === 'string') {
      setAlertMessages([messages])
    } else {
      setAlertMessages(messages)
    }
    setIsLoading(false)

    setTimeout(() => {
      lib.scrollToError(110)
    }, 0)
  }

  const fetchData = async () => {
    try {
      setIsLoading(true)

      const payload = {
        amenities: selectedExtras.map(({ code, quantity }) => ({
          Code: code,
          Quantity: quantity,
        })),
        vehicleGroup: selectedVehicle.car.name,
        rateReference: selectedVehicle.rateReference,
        brandCode: selectedVehicle.brandName,
        acriss: selectedVehicle.car.acriss,
        isPrepaid,
        token: token,
      }

      const response = await postData(
        configuration.endpointGetSingleRate,
        payload
      )

      dispatch(setSelectedVehicle(response.vehicle))
      handleSuccess()
    } catch (err) {
      console.error(err)
      handleError([err.message])
    }
  }

  const handleShowPayment = (value) => {
    // Only set payment visibility if there's remaining cost AND value is true
    setShowPayment(value && mbData?.hasRemainingCost)
  }
  useEffect(() => {
    if (mbData) {
      handleShowPayment(true)
    }
  }, [mbData])

  const shouldShowPayment =
    showPayment &&
    (isPrepaid ||
      internationalEngine ||
      selectedVehicle?.car?.requireCreditCard ||
      (mbData?.redeemEnabled && mbData?.hasRemainingCost))

  // Add safety check to prevent errors during navigation
  const prepaidPrice = selectedVehicle?.car?.prepaidPrice || {}
  const returnPrice = selectedVehicle?.car?.returnPrice || {}

  const getRatePrice = () => {
    if (!selectedVehicle || !selectedVehicle.car) {
      return {}
    }

    if (isPrepaid) {
      return prepaidPrice
    }
    return returnPrice
  }

  // Fixed implementation that doesn't use hooks directly
  const calculateHasRemainingCost = () => {
    if (!selectedVehicle || !selectedVehicle?.car) {
      return true
    }

    const totalPriceToMiles = () => {
      const conversionRate = milesNBonusAccount?.rate || 300
      return Math.round(getRatePrice().prepaidCharge * conversionRate)
    }

    const totalMiles = totalPriceToMiles()
    // Only consider miles if redemption is enabled
    const effectiveMiles = mbData?.redeemEnabled
      ? selectedMiles || mbData?.redeemAmount || 0
      : 0
    const hasRemainingCost = totalMiles > effectiveMiles

    return hasRemainingCost
  }

  const showBillingNonPrepaid = () => {
    if (internationalEngine) {
      // isGREngine is false
      setShowBillingInformation(true)
      handleShowPayment(true)
    } else if (selectedVehicle?.car?.requireCreditCard) {
      // Domestic engine (isGREngine is true) and requires credit card
      setShowBillingInformation(true)
      handleShowPayment(true)
    } else {
      // Domestic engine and doesn't require credit card
      setShowBillingInformation(false)
      handleShowPayment(false)
    }
  }

  useEffect(() => {
    if (isPrepaid) {
      setShowBillingInformation(true)
      handleShowPayment(true)
    } else {
      showBillingNonPrepaid()
    }
  }, [milesNBonusAccount?.fFN])

  const handleCloseOTPModal = () => {
    setOpenMilesNCashOTPModal(false)
    setIsLoading(false)
  }

  const paymentShowIframe = (url) => {
    console.log('paymentShowIframe')
    setTimeout(() => {
      document.getElementById('payment-iframe-frame').style.display = 'block'
      document.getElementById('payment-iframe-frame').src = url
    }, 0)

    setTimeout(() => {
      lib.scrollToElem($('main'), 100)
      setIsLoading(false)
    }, 100)
  }

  const startNotifyTimer = (e) => {
    console.log('startNotifyTimer', e)

    setIsLoading(true)

    const { checkUrl, iframeUrl } = e.detail

    let start = 0
    const timer = window.setInterval(async () => {
      console.log('interval', start)

      if (start > 10) {
        console.log('start larger than 10')
        window.clearInterval(timer)
        document.getElementById('payment-iframe-frame').src = iframeUrl
        return
      }

      try {
        const response = await getData(checkUrl)

        if (response) {
          const { success, notified } = response

          if (success && notified) {
            console.log('success && notified')
            window.clearInterval(timer)
            document.getElementById('payment-iframe-frame').src = iframeUrl
            setTimeout(() => {
              setIsLoading(false)
            }, 1000)
          }
        }
      } catch (err) {
        console.error(err)
        handleError([err.message])
      }

      start += 1
    }, 1000)
  }

  const paymentRemoveIframe = (e) => {
    console.log('paymentRemoveIframe', e)

    setIsLoading(true)

    document.getElementById('payment-iframe-frame').style.display = 'none'

    const { errors, url } = e.detail
    if (url) {
      window.location.assign(url)
      return false
    }

    setRemoteUrl('')
    setIsLoading(false)
    setAlertMessages([dictionary.defaultErrorMessage])
    // if errors array is empty or
    // has just one item with empty string
    if (errors.length && lib.hasValue(errors[0])) {
      setAlertMessages(errors)
    }

    // lib.scrollToElem(document.querySelector(this.refs.strip), 100)
    lib.scrollToTop()
  }

  const iframeProcessCompleted = () => {
    setIsLoading(false)
  }

  const appendNoCardData = (payload, data) => {
    Object.assign(payload, {
      mobilePhone: data.mobilePhone,
      mobileDialingCode: data.mobileDialingCode,
      landPhone: data.landPhone,
      landDialingCode: data.landDialingCode,
    })
  }

  const appendCreditCardData = (payload, data) => {
    Object.assign(payload, {
      mobilePhone: data.mobilePhone,
      mobileDialingCode: data.mobileDialingCode,
      landPhone: data.landPhone,
      landDialingCode: data.landDialingCode,
      addressStreet: data.address,
      addressNumber: data.streetNumber,
      address2: data.addressAlt,
      postalCode: data.postalCode,
      city: data.city,
      country: data.country.text,
      region: data.region.text,
      addressLine: data.addressLine,
      additionalAddressLine: data.additionalAddressLine,
      cardNumber: data.cardNumber,
      cardHolder: data.cardHolder,
      cardExpirationMonth: data.expiryMonth,
      cardExpirationYear: data.expiryYear,
      cardSecureCode: data.cvv,
    })
  }

  useEffect(() => {
    const handleStartNotifyTimer = (e) => startNotifyTimer(e)
    const handlePaymentRemoveIframe = (e) => paymentRemoveIframe(e)
    const handleIframeProcessCompleted = (e) => iframeProcessCompleted(e)

    document.addEventListener('startNotifyTimer', handleStartNotifyTimer)
    document.addEventListener('paymentRemoveIframe', handlePaymentRemoveIframe)
    // prettier-ignore
    document.addEventListener('iframeProcessCompleted', handleIframeProcessCompleted)

    // prettier-ignore
    return () => {
      document.removeEventListener('startNotifyTimer', handleStartNotifyTimer)
      document.removeEventListener('paymentRemoveIframe', handlePaymentRemoveIframe)
      document.removeEventListener('iframeProcessCompleted', handleIframeProcessCompleted)
    }
  }, [])

  useEffect(() => {
    const commentsElement = document.getElementById('comments')
    if (commentsElement) {
      if (commentsElement.value.trim() !== '') {
        commentsElement.classList.add('has-value')
      } else {
        commentsElement.classList.remove('has-value')
      }
    }

    flightList.length > 0
      ? dispatch(setHasFlightNumber(true))
      : dispatch(setHasFlightNumber(false))
  }, [])

  useEffect(() => {
    const handleMBLogin = (event) => {
      const isLoggedIn = event.detail.isLoggedIn
      if (!isLoggedIn) {
        dispatch(resetMilesNBonusAccount())
        dispatch(resetMBData())
      } else {
        dispatch(setMilesNBonusAccount(event.detail.data))
      }
      fetchData()
    }
    window.addEventListener('loggedIn', handleMBLogin)

    return () => {
      window.removeEventListener('loggedIn', handleMBLogin)
    }
  }, [])

  const iframeStyles = {
    border: 'none',
    width: '100%',
    height: '450px',
    overflow: 'auto',
  }

  return (
    <>
      {selectedVehicle && (
        <div className="page-container">
          {alertMessages?.length > 0 && <Alert messages={alertMessages} />}
          <div style={{ display: !remoteUrl ? 'block' : 'none' }}>
            <Upsell handleSuccess={handleSuccess} handleError={handleError} />
          </div>

          <div className="s-booking-form__separator s-booking-form__separator--mobile"></div>

          <div className="row">
            <div className="col-lg-7">
              <div
                className="payment-iframe-container"
                style={{ display: remoteUrl ? 'block' : 'none' }}>
                {isLoading && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress color="inherit" size={24} />
                  </div>
                )}
                <iframe
                  id="payment-iframe-frame"
                  title="payment"
                  style={iframeStyles}></iframe>
              </div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                style={{ display: !remoteUrl ? 'block' : 'none' }}>
                <DriverDetails
                  ref={driverDetailsRef}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  trigger={trigger}
                  // setIsMarketing={(value) => setIsMarketing(value)}
                  onEmailBlur={handleEmailBlur}
                />
                <div className="s-booking-form__separator"></div>
                {showBillingInformation && (
                  <BillingAddress
                    ref={billingAddressRef}
                    configuration={configuration}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                  />
                )}
                <FlightDetails
                  ref={flightDetailsRef}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  trigger={trigger}
                  hasFlightNumber={hasFlightNumber}
                />
                <div className="s-booking-form__separator"></div>
                <CommentsSection
                  ref={commentsSectionRef}
                  control={control}
                  errors={errors}
                />
                {showMilesNBonus({
                  selectedVehicle,
                  milesNBonusAccount,
                  isPrepaid,
                }) && (
                  <>
                    <div className="s-booking-form__separator"></div>
                    <MilesNCash
                      control={control}
                      errors={errors}
                      setValue={setValue}
                      isLoading={isLoading}
                      triggerLoginModal={() =>
                        window.dispatchEvent(new CustomEvent('openLoginModal'))
                      }
                      totalPrice={getRatePrice().prepaidCharge}
                    />
                  </>
                )}
                {shouldShowPayment && (
                  <div className="s-booking-form__separator"></div>
                )}
                {shouldShowPayment && (
                  <Payment control={control} errors={errors} />
                )}
                <div className="mt-40">
                  <Terms
                    endpoint={configuration.endpointTermsNConditions}
                    dictionary={dictionary}
                    control={control}
                    termsOnly={false}
                  />
                </div>
                <div className="s-booking-form__submit">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={24} />
                    ) : (
                      dictionary.formSubmitButton
                    )}
                  </button>
                </div>
                <SummaryMobilePanel />
              </form>
            </div>
            <div className="col-lg-4 offset-lg-1 d-none d-lg-block">
              <Sidebar isLoading={isLoading} />
            </div>
          </div>
        </div>
      )}

      <MilesNCashOTPModal
        open={openMilesNCashOTPModal}
        handleClose={handleCloseOTPModal}
        selectedMiles={selectedMiles}
        hasRemainingCost={calculateHasRemainingCost()}
        verifyOTP={verifyOTP}
        requestOTP={requestOTP}
      />

      {/* Reservation Check Modal */}
      <div
        className={`modal fade ${showReservationModal ? 'show' : ''}`}
        style={{ display: showReservationModal ? 'block' : 'none' }}
        tabIndex="-1"
        role="dialog"
        id="modalReservationCheck">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <button
                type="button"
                className="btn btn-close"
                onClick={() => setShowReservationModal(false)}
                aria-label={dictionary.reservationDuplicateButtonLabel}>
                <i className="icon-x"></i>
              </button>
              <div className="modal-title">
                <div>{dictionary.reservationDuplicateTitle}</div>
              </div>
            </div>
            <div className="modal-body">
              {dictionary.reservationDuplicateMessage}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowReservationModal(false)}
                title={dictionary.reservationDuplicateButtonLabel}>
                {dictionary.reservationDuplicateButtonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Modal backdrop */}
      {showReservationModal && <div className="modal-backdrop fade show"></div>}
    </>
  )
}

export default BookingStep3
